import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Skeleton } from '../ui/skeleton';
import { ArrowDown, Users, User as UserIcon } from 'lucide-react';
import { useReportingChain, useMyReportingChain } from '../../hooks';

interface SubjectPerson {
  id: string;
  name: string;
  profilePictureUrl?: string;
  organizationRole?: string;
}

interface ReportingChainProps {
  userId?: string;
  useCurrentUser?: boolean;
  subject?: SubjectPerson;
}

interface PersonNodeData {
  id: string;
  name?: string;
  displayName?: string;
  profilePictureUrl?: string;
  photoURL?: string;
  organizationRole?: string;
}

interface PersonNodeCardProps {
  person: PersonNodeData;
  onClick?: () => void;
  isCurrentPerson?: boolean;
}

const PersonNodeCard: React.FC<PersonNodeCardProps> = ({ person, onClick, isCurrentPerson = false }) => {
  const displayName = person.name || person.displayName || '';
  const photoSrc = person.profilePictureUrl || person.photoURL;
  const cardContent = (
    <div className="flex items-center gap-2">
      <Avatar className="h-10 w-10">
        {photoSrc ? (
          <AvatarImage src={photoSrc} alt={displayName} />
        ) : null}
        <AvatarFallback className="bg-primary/10 text-primary text-sm">
          {displayName?.charAt(0) || <UserIcon className="h-5 w-5" />}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-foreground text-sm truncate">{displayName}</p>
        {person.organizationRole && (
          <p className="text-xs text-muted-foreground truncate">{person.organizationRole}</p>
        )}
      </div>
    </div>
  );

  if (onClick && !isCurrentPerson) {
    return (
      <button
        onClick={onClick}
        className="w-full text-left p-2 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer"
      >
        {cardContent}
      </button>
    );
  }

  return (
    <div className="w-full p-2 rounded-lg border-2 border-primary/20 bg-primary/5">
      {cardContent}
    </div>
  );
};

const ConnectorArrow: React.FC = () => {
  return (
    <div className="flex flex-col items-center py-1">
      <div className="w-px h-3 bg-border" />
      <ArrowDown className="h-4 w-4 text-muted-foreground" />
      <div className="w-px h-1 bg-border" />
    </div>
  );
};

const LoadingSkeleton: React.FC = () => {
  return (
    <div className="space-y-3">
      <div className="p-4 rounded-lg border">
        <div className="flex items-center gap-4">
          <Skeleton className="h-12 w-12 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
      </div>
    </div>
  );
};

const EmptyState: React.FC = () => {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <Users className="h-12 w-12 text-muted-foreground/50 mb-3" />
      <p className="text-sm text-muted-foreground">
        No managers in reporting chain
      </p>
      <p className="text-xs text-muted-foreground mt-1">
        This user is at the top of the organization
      </p>
    </div>
  );
};

export const ReportingChain: React.FC<ReportingChainProps> = ({ userId, useCurrentUser = false, subject }) => {
  const navigate = useNavigate();
  
  const userChainQuery = useReportingChain(userId || '');
  const myChainQuery = useMyReportingChain();
  
  const { data, isLoading, error } = useCurrentUser ? myChainQuery : userChainQuery;

  const handleManagerClick = (managerId: string) => {
    navigate(`/people/${managerId}`);
  };

  const getManagersForDisplay = (): PersonNodeData[] => {
    if (!data) return [];
    return data.slice().reverse();
  };

  const managersToDisplay = getManagersForDisplay();

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Users className="h-4 w-4" />
          Reporting Chain
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && <LoadingSkeleton />}
        
        {error && (
          <p className="text-sm text-destructive">
            Failed to load reporting chain
          </p>
        )}
        
        {!isLoading && !error && data && (
          <>
            {managersToDisplay.length === 0 && !subject ? (
              <EmptyState />
            ) : (
              <div>
                {managersToDisplay.map((manager, index) => (
                  <React.Fragment key={manager.id}>
                    <PersonNodeCard
                      person={manager}
                      onClick={() => handleManagerClick(manager.id)}
                    />
                    {(index < managersToDisplay.length - 1 || subject) && <ConnectorArrow />}
                  </React.Fragment>
                ))}
                {subject && (
                  <PersonNodeCard
                    person={subject}
                    isCurrentPerson
                  />
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
